import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
 Container,
 Paper,
 TextField,
 Button,
 Typography,
 Box,
 Alert,
 CircularProgress,
 RadioGroup,
 FormControlLabel,
 Radio,
 FormControl,
 FormLabel,
 Switch,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import api from './api';
import qs from 'qs';
import { useUser } from './UserContext';
import { useTiers } from './TiersContext';
import BusyOverlay from './BusyOverlay';
import {
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC);

const PaymentSection = ({ clubType, onPaymentMethodCreated, paymentError, setPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const [isCreatingPaymentMethod, setIsCreatingPaymentMethod] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: theme.palette.mode === 'dark' ? '#ffffff' : '#424770',
        backgroundColor: 'transparent',
        '::placeholder': {
          color: theme.palette.mode === 'dark' ? '#aaa' : '#aab7c4',
        },
      },
      invalid: {
        color: theme.palette.error.main,
        iconColor: theme.palette.error.main,
      },
    },
  };

  const getClubTypeDisplay = () => {
    switch (clubType) {
      case 'starter':
        return { name: 'Starter Club', price: '$5/month (save $3!)', trial: '3-day free trial' };
      case 'small':
        return { name: 'Small Club', price: '$10/month (save $5!)', trial: '3-day free trial' };
      case 'medium':
        return { name: 'Medium Club', price: '$25/month (save $5!)', trial: '3-day free trial' };
      default:
        return { name: 'Unknown', price: '$0/month', trial: '' };
    }
  };

  const handleCardChange = async (event) => {
    if (event.complete && !event.error) {
      // Card is complete and valid, create payment method
      if (!stripe || !elements || isCreatingPaymentMethod) {
        return;
      }

      setIsCreatingPaymentMethod(true);
      setPaymentError('');

      const card = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: card,
      });

      if (error) {
        setPaymentError(error.message);
        setPaymentMethodId(null);
        onPaymentMethodCreated(null);
      } else {
        setPaymentMethodId(paymentMethod.id);
        onPaymentMethodCreated(paymentMethod.id);
      }

      setIsCreatingPaymentMethod(false);
    } else if (event.error) {
      setPaymentError(event.error.message);
      setPaymentMethodId(null);
      onPaymentMethodCreated(null);
    } else {
      // Card is incomplete
      setPaymentError('');
      setPaymentMethodId(null);
      onPaymentMethodCreated(null);
    }
  };

  const clubTypeInfo = getClubTypeDisplay();

  if (!clubType) {
    return null;
  }

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Payment Information
      </Typography>

      {/* Subscription Summary */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" gutterBottom>
          Subscription Summary
        </Typography>
        <Typography variant="body2">
          <strong>Plan:</strong> {clubTypeInfo.name}
        </Typography>
        <Typography variant="body2">
          <strong>Price:</strong> {clubTypeInfo.price}
        </Typography>
        {clubTypeInfo.trial && (
          <Typography variant="body2" color="success.main">
            <strong>Trial:</strong> {clubTypeInfo.trial}
          </Typography>
        )}
      </Box>

      {/* Trial Information */}
      <Box sx={{
        mb: 3,
        p: 2,
        bgcolor: 'background.default',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="subtitle1" gutterBottom>
          🎉 Start Your Free Trial
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try your club for 3 days absolutely free! Your subscription will begin after the trial period ends.
          You can cancel anytime during the trial with no charge.
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your card will be charged after the 3 day trial period ends.
      </Typography>

      {/* Card Input */}
      <Box sx={{
        p: 2,
        border: 1,
        borderColor: paymentMethodId ? 'success.main' : 'divider',
        borderRadius: 1,
        mb: 2,
        bgcolor: 'background.paper',
        position: 'relative'
      }}>
        <CardElement
          options={cardElementOptions}
          onChange={handleCardChange}
        />
        {isCreatingPaymentMethod && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            right: 16,
            transform: 'translateY(-50%)'
          }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {paymentMethodId && !isCreatingPaymentMethod && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            right: 16,
            transform: 'translateY(-50%)'
          }}>
            <CheckCircleIcon color="success" />
          </Box>
        )}
      </Box>

      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        No charge today. You'll be billed {clubTypeInfo.price} after your 3 day trial ends. Cancel anytime.
      </Typography>
    </Box>
  );
};

const ClubFormContent = () => {
  const { user, setUser } = useUser();
  const tiers = useTiers();
  const { clubShortName } = useParams();
  const [clubName, setClubName] = useState('');
  const [clubGuid, setClubGuid] = useState('');
  const [clubShortNameState, setClubShortNameState] = useState('');
  const [originalClubShortName, setOriginalClubShortName] = useState(''); // Track original short name
  const [clubType, setClubType] = useState('');
  const [clubStripeSubscriptionStatus, setClubStripeSubscriptionStatus] = useState('');
  const [clubStripeCancelsAtPeriodEnd, setClubStripeCancelsAtPeriodEnd] = useState(false);
  const [clubStripeCurrentPeriodEnds, setClubStripeCurrentPeriodEnds] = useState(0);
  const [shortNameStatus, setShortNameStatus] = useState('');
  const [shortNameError, setShortNameError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [clubMembers, setClubMembers] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [contestCount, setContestCount] = useState(0);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showDeleteFreeDialog, setShowDeleteFreeDialog] = useState(false);
  const [isDeletingFree, setIsDeletingFree] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [targetClubType, setTargetClubType] = useState('');
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [clubIsFree, setClubIsFree] = useState(false);
  const isFreeClub = clubType === 'free' || clubIsFree;
  const [upgradePaymentMethodId, setUpgradePaymentMethodId] = useState(null);
  const [upgradePaymentError, setUpgradePaymentError] = useState('');
  const [useFreeCredit, setUseFreeCredit] = useState(false);
  const [signUpCode, setSignUpCode] = useState('');
  const [clubMaxMembers, setClubMaxMembers] = useState(null);
  const [clubMaxContests, setClubMaxContests] = useState(null);
  const [clubMonthlyPrice, setClubMonthlyPrice] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteExpires, setInviteExpires] = useState(null); // Unix seconds, null = never
  const [inviteDays, setInviteDays] = useState('7');
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
  const [isRevokingInvite, setIsRevokingInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [hideEmails, setHideEmails] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [csvRows, setCsvRows] = useState([]);
  const [csvParseError, setCsvParseError] = useState('');
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { sent, skipped } on success
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const navigate = useNavigate();

  // Check if sign up code is required
  const requiresSignUpCode = process.env.REACT_APP_USES_SIGN_UP_CODE === 'true';

  const handleDeleteMember = (email) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      const updatedMembers = clubMembers.filter(m => m.userEmail.toLowerCase() !== email.toLowerCase());
      setClubMembers(updatedMembers);

      // Update club type if needed
      if (!isEditMode) {
        if (updatedMembers.length >= 90) {
          setClubType('medium');
        } else if (clubType === 'medium') {
          setClubType('small');
        }
        // Don't auto-downgrade from small to starter — user must choose starter explicitly
      }
    }
  };

  const WEB_URL = process.env.REACT_APP_WEB_URL;
  const [debounceTimer, setDebounceTimer] = useState(null);

  const [majorError, setMajorError] = useState(false);

  const loadClubData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/club/${clubShortName}`, {
        headers: {
          'Authentication': user.authentication
        }
      });

      if (response.status === 200) {
        const clubData = response.data.result;
        setClubName(clubData.clubName);
        setClubGuid(clubData.clubGuid);
        setClubShortNameState(clubData.clubShortName);
        setOriginalClubShortName(clubData.clubShortName); // Store original short name
        setClubType(clubData.clubType);
        setClubMembers(clubData.clubMembers || []);
        setUserRole(clubData.userRole);
        setContestCount(clubData.contests.length ?? 0);

        setClubStripeSubscriptionStatus(clubData.clubStripeSubscriptionStatus);
        setClubStripeCancelsAtPeriodEnd(clubData.clubStripeCancelsAtPeriodEnd);
        setClubStripeCurrentPeriodEnds(clubData.clubStripeCurrentPeriodEnds);
        setClubMaxMembers(clubData.maxMembers ?? null);
        setClubMaxContests(clubData.maxContests ?? null);
        setClubMonthlyPrice(clubData.monthlyPrice ?? null);
        setClubIsFree(clubData.clubIsFree ?? false);
        setInviteCode(clubData.inviteCode ?? null);
        setInviteExpires(clubData.inviteExpires ?? null);
        setHideEmails(clubData.clubHideEmails ?? false);

        // Set initial short name status to available if unchanged
        setShortNameStatus('available');
      }
    } catch (error) {
      setSubmitError('Failed to load club data');
      setMajorError(true);
    } finally {
      setIsLoading(false);
    }
  },[clubShortName, user.authentication]);

    // Determine if we're in edit mode based on URL params
  useEffect(() => {
    if (clubShortName) {
      setIsEditMode(true);
      loadClubData();
    } else {
      // Create mode — fetch fresh user data so credit counts are current
      api.get('/user', { headers: { 'Authentication': user.authentication } })
        .then(res => {
          if (res.status === 200 && res.data?.result) {
            setUser(prev => ({ ...prev, ...res.data.result }));
          }
        })
        .catch(() => {});
    }
  }, [clubShortName, loadClubData, setUser, user.authentication]);

  const validateShortName = (name) => {
    const validPattern = /^[a-z0-9-]+$/;
    return validPattern.test(name) && name.length > 0;
  };

  const checkShortNameAvailability = async (name) => {
    if (!name || !validateShortName(name)) {
      setShortNameStatus('invalid');
      setShortNameError('Short name must contain only lowercase letters, numbers, and dashes');
      return;
    }

    // If in edit mode and the name hasn't changed, mark as available
    if (isEditMode && name === originalClubShortName) {
      setShortNameStatus('available');
      setShortNameError('');
      return;
    }

    setShortNameStatus('checking');
    setShortNameError('');

    try {
      const response = await api.get(`/clubshortname?name=${encodeURIComponent(name)}`, {
        headers: {
          'Authentication': user.authentication
        }
      });

      if (response.status === 200) {
        setShortNameStatus('available');
        setShortNameError('');
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setShortNameStatus('taken');
        setShortNameError('This short name is already taken');
      } else if (error.response?.status === 400) {
        setShortNameStatus('invalid');
        setShortNameError('Invalid short name');
      } else {
        setShortNameStatus('');
        setShortNameError('Error checking availability');
      }
    }
  };

  const handleShortNameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setClubShortNameState(value);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (value) {
      const timer = setTimeout(() => {
        checkShortNameAvailability(value);
      }, 500);
      setDebounceTimer(timer);
    } else {
      setShortNameStatus('');
      setShortNameError('');
    }
  };

  const handleDownloadMembers = () => {
    if (clubShortName) {
      window.open(`${process.env.REACT_APP_API_URL}/club/${clubShortName}/downloadmembers?token=${user.authentication}`, '_blank');
    }
  };

  const handleGenerateInvite = async () => {
    setIsGeneratingInvite(true);
    try {
      const res = await api.post(`/club/${clubShortName}/invite`,
        new URLSearchParams({ days: inviteDays }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Authentication': user.authentication } }
      );
      setInviteCode(res.data.result.inviteCode);
      setInviteExpires(res.data.result.inviteExpires ?? null);
      setInviteCopied(false);
    } catch {
      alert('Failed to generate invite link. Please try again.');
    } finally {
      setIsGeneratingInvite(false);
    }
  };

  const handleRevokeInvite = async () => {
    if (!window.confirm('Expire this invite link? Anyone with the link will no longer be able to join.')) return;
    setIsRevokingInvite(true);
    try {
      await api.delete(`/club/${clubShortName}/invite`, {
        headers: { 'Authentication': user.authentication }
      });
      setInviteCode(null);
      setInviteExpires(null);
      setInviteCopied(false);
    } catch {
      alert('Failed to revoke invite link. Please try again.');
    } finally {
      setIsRevokingInvite(false);
    }
  };

  const handleCopyInvite = () => {
    const url = `${process.env.REACT_APP_WEB_URL}/join?inviteCode=${inviteCode}`;
    navigator.clipboard.writeText(url).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    });
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvParseError('');
    setCsvRows([]);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) {
        setCsvParseError('CSV must have a header row and at least one data row.');
        return;
      }
      const headers = parseCsvLine(lines[0]).map(h => h.trim().toLowerCase());
      const emailIdx = headers.findIndex(h => h.includes('email'));
      if (emailIdx === -1) {
        setCsvParseError('CSV must include a column with "email" in the header name.');
        return;
      }
      const nameIdx = headers.findIndex(h => ['name', 'fullname', 'full name', 'full_name', 'displayname'].includes(h));
      const firstNameIdx = headers.findIndex(h => ['first name', 'firstname', 'first_name', 'given name', 'givenname'].includes(h));
      const lastNameIdx = headers.findIndex(h => ['last name', 'lastname', 'last_name', 'surname', 'family name'].includes(h));
      const existingEmails = new Set(clubMembers.map(m => m.userEmail.toLowerCase()));
      const seenEmails = new Set(existingEmails);
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);
        const email = cols[emailIdx]?.trim() ?? '';
        if (!email || seenEmails.has(email.toLowerCase())) continue;
        seenEmails.add(email.toLowerCase());
        let name = '';
        if (nameIdx !== -1) {
          name = cols[nameIdx]?.trim() ?? '';
        } else if (firstNameIdx !== -1 || lastNameIdx !== -1) {
          const first = firstNameIdx !== -1 ? (cols[firstNameIdx]?.trim() ?? '') : '';
          const last = lastNameIdx !== -1 ? (cols[lastNameIdx]?.trim() ?? '') : '';
          name = [first, last].filter(Boolean).join(' ');
        }
        rows.push({ userFullName: name || email, userEmail: email });
      }
      if (rows.length === 0) {
        setCsvParseError('No new members found — all emails already exist in the club, or the file was empty.');
        return;
      }
      setCsvRows(rows);
    };
    reader.readAsText(file);
  };

  const handleRemoveCsvRow = (index) => {
    setCsvRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddManualRow = () => {
    const email = manualEmail.trim();
    if (!email) return;
    const existingEmails = new Set(clubMembers.map(m => m.userEmail.toLowerCase()));
    const csvEmails = new Set(csvRows.map(r => r.userEmail.toLowerCase()));
    if (existingEmails.has(email.toLowerCase())) {
      setCsvParseError(`${email} is already a club member.`);
      return;
    }
    if (csvEmails.has(email.toLowerCase())) {
      setCsvParseError(`${email} is already in the list.`);
      return;
    }
    setCsvRows(prev => [...prev, { userFullName: manualName.trim() || email, userEmail: email }]);
    setManualName('');
    setManualEmail('');
    setCsvParseError('');
  };

  const handleSendInvites = async () => {
    setIsSendingInvites(true);
    setInviteResult(null);
    try {
      const res = await api.post(
        `/club/${clubShortName}/invite-emails`,
        { members: csvRows.map(r => ({ name: r.userFullName, email: r.userEmail })) },
        { headers: { 'Content-Type': 'application/json', 'Authentication': user.authentication } }
      );
      const { sent, skipped, inviteCode: newCode, inviteExpires: newExpires } = res.data.result;
      // Update invite link state in case a new one was created server-side
      if (newCode) setInviteCode(newCode);
      if (newExpires !== undefined) setInviteExpires(newExpires);
      setInviteResult({ sent, skipped });
      setCsvRows([]);
    } catch (err) {
      setCsvParseError(err.response?.data || 'Failed to send invitations. Please try again.');
    } finally {
      setIsSendingInvites(false);
    }
  };

  const handleLeaveClub = async () => {
    setIsLeaving(true);
    try {
      await api.delete(`/club/${clubShortName}/leave`);
      navigate('/dashboard');
    } catch (err) {
      setIsLeaving(false);
      setShowLeaveDialog(false);
      alert(err.response?.data || 'Failed to leave club. Please try again.');
    }
  };

  const ensureOwnerInDisplayList = (memberList) => {
    // Check if current user is already in the list
    const currentUserInList = memberList.find(member =>
      member.userEmail.toLowerCase() === user.userEmail.toLowerCase()
    );

    if (currentUserInList) {
      // Move current user to top if not already there
      const filteredList = memberList.filter(member =>
        member.userEmail.toLowerCase() !== user.userEmail.toLowerCase()
      );
      return [currentUserInList, ...filteredList];
    } else {
      // Add current user at the top for display only
      const ownerMember = {
        userFullName: user.userFullName,
        userEmail: user.userEmail
      };
      return [ownerMember, ...memberList];
    }
  };

  const handleSubscriptionChange = (newClubType) => {
    setTargetClubType(newClubType);
    setUpgradePaymentMethodId(null);
    setUpgradePaymentError('');
    setShowSubscriptionDialog(true);
  };

  const handleConfirmSubscriptionChange = async () => {
    if (isFreeClub && !upgradePaymentMethodId) return;
    setIsUpdatingSubscription(true);
    try {
      const payload = { clubType: targetClubType };
      if (isFreeClub) payload.paymentMethodId = upgradePaymentMethodId;
      const response = await api.post(`/club/${clubShortName}/subscribeupdate`, payload);

      if (response.status === 200) {
        setShowSubscriptionDialog(false);
        setSubmitError('');
        await loadClubData();
        const typeLabel = targetClubType === 'medium' ? 'Medium' : targetClubType === 'small' ? 'Small' : 'Starter';
        alert(`Successfully subscribed to ${typeLabel} Club!`);
      } else {
        setSubmitError('Failed to update subscription');
      }
    } catch (error) {
      setSubmitError('Error updating subscription');
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      const response = await api.get(`/club/${clubShortName}/stripecancel`, {
        headers: {
          'Authentication': user.authentication
        }
      });

      if (response.status === 200) {
        setShowCancelDialog(false);
        setSubmitError('');
        // Reload the data to reflect any changes
        await loadClubData();
        // Show success message
        alert('Subscription canceled successfully. Your club will remain active until the end of the current billing period.');
      } else {
        setSubmitError('Failed to cancel subscription');
      }
    } catch (error) {
      setSubmitError('Error canceling subscription');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleDeleteFreeClub = async () => {
    setIsDeletingFree(true);
    try {
      await api.delete(`/club/${clubShortName}/free`, {
        headers: { 'Authentication': user.authentication }
      });
      navigate('/dashboard');
    } catch (err) {
      setIsDeletingFree(false);
      setShowDeleteFreeDialog(false);
      alert(err.response?.data || 'Failed to delete club. Please try again.');
    }
  };

  const handlePaymentMethodCreated = (methodId) => {
    setPaymentMethodId(methodId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clubName.trim()) {
      setSubmitError('Club name is required');
      return;
    }

    if (!isEditMode) {
      if (!clubShortNameState.trim()) {
        setSubmitError('Club short name is required');
        return;
      }

      if (!clubType) {
        setSubmitError('Please select a club type');
        return;
      }

      if (shortNameStatus !== 'available') {
        setSubmitError('Please choose a valid and available short name');
        return;
      }

      if (clubType !== 'free' && !useFreeCredit && !paymentMethodId) {
        setSubmitError('Please enter valid payment information');
        return;
      }

      if (requiresSignUpCode && !signUpCode.trim()) {
        setSubmitError('Sign up code is required');
        return;
      }
    } else {
      // Edit mode validations
      if (!clubShortNameState.trim()) {
        setSubmitError('Club short name is required');
        return;
      }

      if (shortNameStatus !== 'available') {
        setSubmitError('Please choose a valid and available short name');
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      if (isEditMode) {
        // Handle edit mode with potential short name change
        const data = {
          clubName: clubName.trim(),
          clubGuid: clubGuid,
          clubShortName: clubShortNameState.trim(), // Include short name in edit data
          clubMembers: JSON.stringify(clubMembers),
          hideEmails: hideEmails ? '1' : '0'
        };

        const response = await api.post(`/club/${clubShortName}`, qs.stringify(data), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authentication': user.authentication
          },
        });

        if (response.status === 200) {
          setSubmitError('');

          // Check if short name changed and redirect if needed
          if (clubShortNameState !== originalClubShortName) {
            // Navigate to the new URL
            navigate(`/club/${clubShortNameState}`);
          } else {
            // Reload the data to reflect changes if no redirect needed
            await loadClubData();
          }
        } else {
          setSubmitError(response.statusText || 'Failed to update club');
        }
      } else {
        // Handle create mode with integrated payment

        const clubData = {
          clubName: clubName.trim(),
          clubShortName: clubShortNameState.trim(),
          clubType: clubType,
          clubMembers: JSON.stringify(clubMembers),
          hideEmails: hideEmails ? '1' : '0',
          ...(useFreeCredit
            ? { useFreeCredit: 'true' }
            : {
                paymentMethodId: paymentMethodId,
                priceId: clubType === 'starter'
                  ? process.env.REACT_APP_STRIPE_STARTER_PRICE_ID
                  : clubType === 'small'
                    ? process.env.REACT_APP_STRIPE_SMALL_PRICE_ID
                    : process.env.REACT_APP_STRIPE_MEDIUM_PRICE_ID,
                trialPeriodDays: 3,
              }
          ),
          ...(requiresSignUpCode && { signUpCode: signUpCode.trim() })
        };

        const response = await api.post('/club', qs.stringify(clubData), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authentication': user.authentication
          },
        });

        if (response.status !== 200) {
          throw new Error(response.statusText || 'Failed to create club and process payment');
        }

        // Success! Navigate to the edit club page so the owner can set up invite links, etc.
        navigate(`/club/${clubShortNameState}`);
      }
    } catch (error) {
      setSubmitError(error.response?.data || error.message || 'Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getShortNameIcon = () => {
    switch (shortNameStatus) {
      case 'checking':
        return <CircularProgress size={20} />;
      case 'available':
        return <CheckCircleIcon color="success" />;
      case 'taken':
      case 'invalid':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getShortNameColor = () => {
    switch (shortNameStatus) {
      case 'available':
        return 'success';
      case 'taken':
      case 'invalid':
        return 'error';
      default:
        return 'primary';
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading club data...
        </Typography>
      </Container>
    );
  }

  const isReadOnly = isEditMode && userRole !== 'owner';
  const displayMembers = isEditMode && userRole === 'owner' ? ensureOwnerInDisplayList(clubMembers) : clubMembers;

  const effectiveMaxMembers = clubMaxMembers ?? (tiers?.[clubType]?.maxMembers ?? 99);
  // In edit mode displayMembers includes the owner; in create mode it doesn't (owner is added server-side)
  const memberLimit = isEditMode ? effectiveMaxMembers : effectiveMaxMembers - 1;
  const tooManyMembers = !isReadOnly && clubType !== 'large' && displayMembers.length > memberLimit;
  const openSlots = clubType === 'large' ? Infinity : memberLimit - displayMembers.length;

  // Check if form is valid for submission
  const isFormValid = isEditMode ? (
    clubName.trim() &&
    clubShortNameState.trim() &&
    shortNameStatus === 'available' &&
    !tooManyMembers
  ) : (
    clubName.trim() &&
    clubShortNameState.trim() &&
    clubType &&
    shortNameStatus === 'available' &&
    (clubType === 'free' || useFreeCredit || paymentMethodId) &&
    (!requiresSignUpCode || signUpCode.trim()) &&
    !tooManyMembers
  );

  return (
    <>
      <BusyOverlay open={majorError} message={submitError} />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {isEditMode ? (isReadOnly ? 'Club Details' : 'Edit Club') : 'Create Your Club'}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {isEditMode
              ? (isReadOnly
                  ? 'View your club information and member list.'
                  : 'Update your club information and manage members.')
              : 'Set up your photo contest club and start organizing amazing photography competitions!'
            }
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Club Name"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              margin="normal"
              required={!isReadOnly}
              disabled={isReadOnly}
              placeholder="e.g., Downtown Photography Club"
              inputProps={{ maxLength: 100 }}
            />

            {/* Short name field - now editable in both create and edit modes */}
            <TextField
              fullWidth
              label="Club Short Name"
              value={clubShortNameState}
              onChange={handleShortNameChange}
              margin="normal"
              required={!isReadOnly}
              disabled={isReadOnly}
              placeholder="e.g., downtown-photo"
              helperText={`This will be part of your club's URL: ${WEB_URL}/club/${clubShortNameState || 'your-short-name'}`}
              color={getShortNameColor()}
              InputProps={{
                endAdornment: !isReadOnly ? getShortNameIcon() : null
              }}
            />

            {shortNameError && !isReadOnly && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {shortNameError}
              </Typography>
            )}

            {shortNameStatus === 'available' && !isReadOnly && clubShortNameState && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                {isEditMode && clubShortNameState !== originalClubShortName
                  ? 'This short name is available - your club URL will be updated when you save.'
                  : 'Great! This short name is available.'
                }
              </Typography>
            )}

            {/* Warning message when short name is changed in edit mode */}
            {isEditMode && !isReadOnly && clubShortNameState !== originalClubShortName && shortNameStatus === 'available' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Warning:</strong> Changing your club's short name will immediately update your club's URL from:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', mb: 1 }}>
                  <span style={{ textDecoration: 'line-through', color: '#666' }}>
                    {WEB_URL}/club/{originalClubShortName}
                  </span>
                  <br />
                  <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                    {WEB_URL}/club/{clubShortNameState}
                  </span>
                </Typography>
                <Typography variant="body2">
                  This could disrupt other club members who have bookmarked the old URL or are currently uploading photos to contests.
                  Consider notifying your members about this change beforehand.
                </Typography>
              </Alert>
            )}

            {/* Hide Emails toggle - visible to owners and club creators */}
            {!isReadOnly && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideEmails}
                      onChange={(e) => setHideEmails(e.target.checked)}
                    />
                  }
                  label="Hide Emails"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: '52px', mt: -0.5 }}>
                  {hideEmails
                    ? 'Member email addresses are only visible to you as the club owner.'
                    : 'Member email addresses are visible to all club members.'}
                </Typography>
              </Box>
            )}

            {/* Sign Up Code field - only show in create mode if required */}
            {!isEditMode && requiresSignUpCode && (
              <TextField
                fullWidth
                label="Sign Up Code"
                value={signUpCode}
                onChange={(e) => setSignUpCode(e.target.value)}
                margin="normal"
                required
                placeholder="Enter your sign up code"
                helperText="A valid sign up code is required to create a club"
              />
            )}

            {/* Members Section */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h5">Club Members</Typography>
                {isReadOnly && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<ExitToAppIcon />}
                    onClick={() => setShowLeaveDialog(true)}
                  >
                    Leave Club
                  </Button>
                )}
              </Box>

              {!isReadOnly && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => { setCsvRows([]); setCsvParseError(''); setShowInviteDialog(true); }}
                  >
                    Invite Members
                  </Button>
                  {isEditMode && clubMembers.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadMembers}
                    >
                      Download Members CSV
                    </Button>
                  )}
                </Box>
              )}

              {tooManyMembers && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Too many members — your {clubType} club allows {effectiveMaxMembers} (including you as owner). Please remove {displayMembers.length - memberLimit} member{displayMembers.length - memberLimit !== 1 ? 's' : ''}.
                </Alert>
              )}

              {displayMembers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color={tooManyMembers ? 'error' : 'success.main'} sx={{ mb: 1 }}>
                    {displayMembers.length} member(s){tooManyMembers ? ` (max ${effectiveMaxMembers})` : ''}:
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          {(userRole === 'owner' || !hideEmails) && <TableCell>Email</TableCell>}
                          {!isReadOnly && <TableCell align="center" width={100}>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayMembers.map((member, index) => {
                          const isOwner = isEditMode && userRole === 'owner' && index === 0;
                          const canDelete = !isReadOnly && !isOwner;
                          const isOverLimit = index >= memberLimit;

                          return (
                            <TableRow key={index} sx={{
                              bgcolor: isOverLimit ? 'error.light' : isOwner ? 'action.hover' : 'inherit',
                              opacity: isOverLimit ? 0.85 : 1,
                              '&:hover': { bgcolor: isOverLimit ? 'error.light' : 'action.hover' }
                            }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2">{member.userFullName}</Typography>
                                  {isOwner && (
                                    <Typography variant="caption" color="primary" sx={{ ml: 1 }}>(Owner)</Typography>
                                  )}
                                </Box>
                              </TableCell>

                              {(userRole === 'owner' || !hideEmails) && (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {member.userEmail}
                                  </Typography>
                                </TableCell>
                              )}

                              {!isReadOnly && (
                                <TableCell align="center">
                                  {canDelete ? (
                                    <Tooltip title="Remove member">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteMember(member.userEmail)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">Owner</Typography>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>

            {/* Invite Link — edit mode, owner only */}
            {isEditMode && userRole === 'owner' && (() => {
              const inviteUrl = inviteCode ? `${process.env.REACT_APP_WEB_URL}/join?inviteCode=${inviteCode}` : null;
              const now = Math.floor(Date.now() / 1000);
              const isExpired = inviteExpires !== null && inviteExpires < now;

              return (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="h5" gutterBottom>Invite Link</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Share this link with anyone you want to invite to join your club. You can expire it at any time.
                  </Typography>

                  {/* Current link display */}
                  {inviteCode && (
                    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: isExpired ? 'error.main' : 'success.main', borderRadius: 1, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                          {inviteUrl}
                        </Typography>
                        <Button size="small" variant="outlined" onClick={handleCopyInvite} sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>
                          {inviteCopied ? 'Copied!' : 'Copy'}
                        </Button>
                      </Box>
                      <Typography variant="caption" color={isExpired ? 'error' : 'text.secondary'}>
                        {isExpired
                          ? 'Expired — generate a new link below'
                          : inviteExpires
                            ? `Expires ${new Date(inviteExpires * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : 'Never expires'
                        }
                      </Typography>
                    </Box>
                  )}

                  {/* Generate / regenerate controls */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      select
                      size="small"
                      label="Expires in"
                      value={inviteDays}
                      onChange={(e) => setInviteDays(e.target.value)}
                      sx={{ minWidth: 160 }}
                      SelectProps={{ native: true }}
                    >
                      <option value="1">1 day</option>
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                      <option value="never">Never</option>
                    </TextField>
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleGenerateInvite}
                      disabled={isGeneratingInvite}
                    >
                      {isGeneratingInvite ? 'Generating…' : inviteCode ? 'Generate New Link' : 'Generate Link'}
                    </Button>
                    {inviteCode && !isExpired && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRevokeInvite}
                        disabled={isRevokingInvite}
                      >
                        {isRevokingInvite ? 'Expiring…' : 'Expire Now'}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })()}

            {/* Club Type Selection - only in create mode */}
            {!isEditMode && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend">
                    <Typography variant="h6" gutterBottom>
                      Select Club Type
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    value={clubType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setClubType(newType);
                      const credits = newType === 'starter'
                        ? (user?.userFreeStarterClubCredits ?? 0)
                        : newType === 'small'
                          ? (user?.userFreeSmallClubCredits ?? 0)
                          : newType === 'medium'
                            ? (user?.userFreeMediumClubCredits ?? 0)
                            : 0;
                      setUseFreeCredit(credits > 0);
                      setPaymentMethodId(null);
                    }}
                    sx={{ mt: 1 }}
                  >
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'free' ? 'action.selected' : 'background.default',
                      opacity: user?.userHasFreeClub ? 0.5 : 1,
                    }}>
                      <FormControlLabel
                        value="free"
                        disabled={Boolean(user?.userHasFreeClub)}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Free Club — Forever Free
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user?.userHasFreeClub
                                ? 'Only one free club per user'
                                : 'Perfect for trying Snapperish: up to 5 members, 1 contest, 1 gallery, 5 photos per member. No credit card required.'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>

                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'starter' ? 'action.selected' : 'background.default'
                    }}>
                      <FormControlLabel
                        value="starter"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Starter Club -{' '}
                              {(user?.userFreeStarterClubCredits ?? 0) > 0
                                ? `${user.userFreeStarterClubCredits} free credit${user.userFreeStarterClubCredits !== 1 ? 's' : ''} or $5/month (save $3!)`
                                : '$5/month (save $3!)'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The Snapperish Starter Club supports up to 25 members, 1 ongoing contest with up
                              to 5 photos per member. You can cancel at any time.
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>

                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'small' ? 'action.selected' : 'background.default'
                    }}>
                      <FormControlLabel
                        value="small"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Small Club -{' '}
                              {(user?.userFreeSmallClubCredits ?? 0) > 0
                                ? `${user.userFreeSmallClubCredits} free credit${user.userFreeSmallClubCredits !== 1 ? 's' : ''} or $10/month (save $5!)`
                                : '$10/month (save $5!)'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The Snapperish Small Club supports 1 to 99 members, 1 ongoing contest with up
                              to 10 photos per member. You can cancel at any time.
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>

                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'medium' ? 'action.selected' : 'background.default'
                    }}>
                      <FormControlLabel
                        value="medium"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Medium Club -{' '}
                              {(user?.userFreeMediumClubCredits ?? 0) > 0
                                ? `${user.userFreeMediumClubCredits} free credit${user.userFreeMediumClubCredits !== 1 ? 's' : ''} or $25/month (save $5!)`
                                : '$25/month (save $5!)'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The Snapperish Medium Club supports up to 250 members with 2 ongoing contests and up
                              to 15 photos per member. You can cancel at any time.
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </RadioGroup>
                </FormControl>

                <Box sx={{ mt: 1 }}>
                  <Link to="/pricing" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                      View detailed pricing and features →
                    </Typography>
                  </Link>
                </Box>
              </Box>
            )}

            {/* Payment method choice - only in create mode when a club type is selected */}
            {!isEditMode && clubType && (() => {
              const credits = clubType === 'starter'
                ? (user?.userFreeStarterClubCredits ?? 0)
                : clubType === 'small'
                  ? (user?.userFreeSmallClubCredits ?? 0)
                  : (user?.userFreeMediumClubCredits ?? 0);
              if (credits < 1) return null;
              return (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      <Typography variant="h6" gutterBottom>
                        Payment
                      </Typography>
                    </FormLabel>
                    <RadioGroup
                      value={useFreeCredit ? 'free' : 'card'}
                      onChange={(e) => {
                        setUseFreeCredit(e.target.value === 'free');
                        setPaymentMethodId(null);
                      }}
                      sx={{ mt: 1 }}
                    >
                      <Box sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        bgcolor: useFreeCredit ? 'action.selected' : 'background.default'
                      }}>
                        <FormControlLabel
                          value="free"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                Use Free Credit
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                You have {credits} free {clubType} club credit{credits !== 1 ? 's' : ''} available. No payment required.
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                      <Box sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        bgcolor: !useFreeCredit ? 'action.selected' : 'background.default'
                      }}>
                        <FormControlLabel
                          value="card"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                Subscribe with Card
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ${clubType === 'starter' ? '5' : clubType === 'small' ? '10' : '25'}/month after a 3-day free trial. Cancel anytime.
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </Box>
              );
            })()}

            {/* Payment Section - only in create mode when paying with card */}
            {!isEditMode && clubType && clubType !== 'free' && !useFreeCredit && (
              <PaymentSection
                clubType={clubType}
                onPaymentMethodCreated={handlePaymentMethodCreated}
                paymentError={paymentError}
                setPaymentError={setPaymentError}
              />
            )}

            {/* Subscription Plan Management - in edit mode */}
            {isEditMode && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Subscription Plan
                </Typography>
                {clubType !== 'free' && (
                  <Box sx={{marginBottom: 1}}>
                    <Typography variant="body" gutterBottom >
                      {clubStripeCancelsAtPeriodEnd ? (
                        <span style={{fontStyle: 'italic'}}>
                          {`Subscription ends ${new Date(clubStripeCurrentPeriodEnds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        </span>
                      ) : clubStripeSubscriptionStatus === 'trialing' ? (
                        <span style={{fontStyle: 'italic'}}>
                          {`Trial ends ${new Date(clubStripeCurrentPeriodEnds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        </span>
                      ) : (
                        <span style={{fontStyle: 'italic'}}>
                          {`Next payment ${new Date(clubStripeCurrentPeriodEnds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}

                {clubType === 'free' ? (
                  <>
                    {/* Free Club - current plan + upgrade options */}
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: 'action.selected',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">Free Club</Typography>
                        <Typography variant="body2" color="text.secondary">
                          5 members, 1 contest, 1 gallery, 5 photos per member. Free forever.
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="primary" fontWeight="bold">Current Plan</Typography>
                    </Box>

                    {/* Upgrade to Starter */}
                    <Box sx={{
                      border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2,
                      bgcolor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">Starter Club - $5/month (save $3!)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to 25 members, 1 active contest, up to 5 photos per member
                        </Typography>
                      </Box>
                      {userRole === 'owner' && (
                        <Button variant="outlined" size="small" onClick={() => handleSubscriptionChange('starter')}>
                          Upgrade to Starter Club
                        </Button>
                      )}
                    </Box>

                    {/* Upgrade to Small */}
                    <Box sx={{
                      border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2,
                      bgcolor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">Small Club - $10/month (save $5!)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to 99 members, 1 active contest, up to 10 photos per member
                        </Typography>
                      </Box>
                      {userRole === 'owner' && (
                        <Button variant="outlined" size="small" onClick={() => handleSubscriptionChange('small')}>
                          Upgrade to Small Club
                        </Button>
                      )}
                    </Box>

                    {/* Upgrade to Medium */}
                    <Box sx={{
                      border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2,
                      bgcolor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">Medium Club - $25/month (save $5!)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to 250 members, 2 active contests, up to 15 photos per member
                        </Typography>
                      </Box>
                      {userRole === 'owner' && (
                        <Button variant="outlined" size="small" onClick={() => handleSubscriptionChange('medium')}>
                          Upgrade to Medium Club
                        </Button>
                      )}
                    </Box>
                  </>
                ) : clubType === 'large' ? (
                  <>
                    {/* Large Club - current plan display */}
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: 'action.selected',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Large Club (Custom Plan)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to {clubMaxContests != null ? `${clubMaxContests} active contests` : '—'},{' '}
                          up to {clubMaxMembers != null ? `${clubMaxMembers} members` : '—'}
                        </Typography>
                        {clubMonthlyPrice != null && (
                          <Typography variant="body2" color="text.secondary">
                            ${clubMonthlyPrice}/month
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        Current Plan
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      To change your large club plan, please <Link to="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>contact us</Link>.
                    </Typography>
                  </>
                ) : (
                  <>
                    {/* Starter Club Option */}
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'starter' ? 'action.selected' : 'background.default',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Starter Club - $5/month (save $3!)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to 25 members, 1 active contest, up to 5 photos per member
                        </Typography>
                        {contestCount > 1 && (
                          <Typography variant="body2" color="error">
                            Your oldest {contestCount - 1 === 1 ? 'contest' : `${contestCount - 1} contests`} will be deleted!
                          </Typography>
                        )}
                        {clubMembers.length > 25 && (
                          <Typography variant="body2" color="error">
                            Your last {clubMembers.length - 25} member{clubMembers.length - 25 !== 1 ? 's' : ''} (alphabetically) will be removed!
                          </Typography>
                        )}
                      </Box>
                      {clubType === 'starter' && !clubStripeCancelsAtPeriodEnd ? (
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          Current Plan
                        </Typography>
                      ) : userRole === 'owner' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSubscriptionChange('starter')}
                        >
                          {clubStripeCancelsAtPeriodEnd ? 'Subscribe to Starter Club' : 'Downgrade to Starter Club'}
                        </Button>
                      ) : null}
                    </Box>

                    {/* Small Club Option */}
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'small' ? 'action.selected' : 'background.default',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Small Club - $10/month (save $5!)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to 99 members, 1 active contest, up to 10 photos per member
                        </Typography>
                        {contestCount > 1 && (
                          <Typography variant="body2" color="error">
                            Your oldest {contestCount - 1 === 1 ? 'contest' : `${contestCount - 1} contests`} will be deleted!
                          </Typography>
                        )}
                      </Box>
                      {clubType === 'small' && !clubStripeCancelsAtPeriodEnd ? (
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          Current Plan
                        </Typography>
                      ) : userRole === 'owner' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSubscriptionChange('small')}
                        >
                          {clubStripeCancelsAtPeriodEnd ? 'Subscribe to Small Club' : clubType === 'starter' ? 'Upgrade to Small Club' : 'Downgrade to Small Club'}
                        </Button>
                      ) : null}
                    </Box>

                    {/* Medium Club Option */}
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: clubType === 'medium' ? 'action.selected' : 'background.default',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Medium Club - $25/month (save $5!)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Supports up to 250 members, 2 active contests, up to 15 photos per member
                        </Typography>
                        {contestCount > 2 && (
                          <Typography variant="body2" color="error">
                            Your oldest {contestCount - 2 === 1 ? 'contest' : `${contestCount - 2} contests`} will be deleted!
                          </Typography>
                        )}
                      </Box>
                      {clubType === 'medium' && !clubStripeCancelsAtPeriodEnd ? (
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          Current Plan
                        </Typography>
                      ) : userRole === 'owner' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSubscriptionChange('medium')}
                        >
                          {clubStripeCancelsAtPeriodEnd ? 'Subscribe to Medium Club' : (clubType === 'starter' || clubType === 'small') ? 'Upgrade to Medium Club' : 'Downgrade to Medium Club'}
                        </Button>
                      ) : null}
                    </Box>
                  </>
                )}

                {userRole === 'owner' && clubType === 'free' && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setShowDeleteFreeDialog(true)}
                    >
                      Delete Free Club
                    </Button>
                  </Box>
                )}

                {userRole === 'owner' && clubType !== 'free' && !clubStripeCancelsAtPeriodEnd && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setShowCancelDialog(true)}
                    >
                      Cancel Subscription
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {submitError}
              </Alert>
            )}

            {!isReadOnly && (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !isFormValid}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {isEditMode ? 'Saving Changes...' : (clubType === 'free' || useFreeCredit) ? 'Creating Club...' : 'Creating Club & Starting Trial...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : clubType === 'free' ? 'Create Free Club' : useFreeCredit ? 'Create Club with Free Credit' : 'Create Club & Start Free Trial'
                )}
              </Button>
            )}
          </Box>
        </Paper>

        {/* Subscription Change Confirmation Dialog */}
        <Dialog
          open={showSubscriptionDialog}
          onClose={() => setShowSubscriptionDialog(false)}
          TransitionProps={{ unmountOnExit: true }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isFreeClub
              ? `Subscribe to ${targetClubType === 'medium' ? 'Medium' : targetClubType === 'small' ? 'Small' : 'Starter'} Club`
              : clubStripeCancelsAtPeriodEnd
                ? `Resubscribe to ${targetClubType === 'medium' ? 'Medium' : 'Small'} Club`
                : (targetClubType === 'medium' || (targetClubType === 'small' && clubType === 'starter'))
                  ? `Upgrade to ${targetClubType === 'medium' ? 'Medium' : 'Small'} Club`
                  : `Downgrade to ${targetClubType === 'medium' ? 'Medium' : 'Small'} Club`}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Price:</strong> ${targetClubType === 'medium' ? '25' : targetClubType === 'small' ? '10' : '5'}/month
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Member Limit:</strong> {targetClubType === 'medium' ? '100-250 members' : targetClubType === 'small' ? '1-99 members' : 'up to 25 members'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Active Contests:</strong> {targetClubType === 'medium' ? '2 contests' : '1 contest'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Photos per Member:</strong> {targetClubType === 'medium' ? '15 photos' : '10 photos'}
            </Typography>
            {isFreeClub ? (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your free club will be converted to a paid subscription. You'll be billed ${targetClubType === 'medium' ? '25' : targetClubType === 'small' ? '10' : '5'}/month starting today.
                </Typography>
                <PaymentSection
                  clubType={targetClubType}
                  onPaymentMethodCreated={setUpgradePaymentMethodId}
                  paymentError={upgradePaymentError}
                  setPaymentError={setUpgradePaymentError}
                />
              </>
            ) : (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {clubStripeCancelsAtPeriodEnd
                  ? `Resubscribe to the ${targetClubType === 'medium' ? 'Medium' : 'Small'} Club plan?`
                  : (targetClubType === 'medium' || (targetClubType === 'small' && clubType === 'starter'))
                    ? 'The new subscription takes effect immediately. You will be charged a prorated amount for the remainder of the current period.'
                    : 'The downgrade takes effect immediately. You will be refunded a prorated amount. Excess members will be removed in full name alphabetical order (Adam before Betty).'
                }
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSubscriptionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSubscriptionChange}
              variant="contained"
              disabled={isUpdatingSubscription || (isFreeClub && !upgradePaymentMethodId)}
            >
              {isUpdatingSubscription ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Updating...
                </>
              ) : (
                isFreeClub
                  ? 'Subscribe'
                  : `Confirm ${clubStripeCancelsAtPeriodEnd ? 'Resubscribe' : (targetClubType === 'medium' || (targetClubType === 'small' && clubType === 'starter')) ? 'Upgrade' : 'Downgrade'}`
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Free Club Confirmation Dialog */}
        <Dialog
          open={showDeleteFreeDialog}
          onClose={() => !isDeletingFree && setShowDeleteFreeDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Free Club?</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{clubName}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will permanently delete the club and all its contests, photos, and member data.
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteFreeDialog(false)} disabled={isDeletingFree}>
              Keep Club
            </Button>
            <Button
              onClick={handleDeleteFreeClub}
              variant="contained"
              color="error"
              disabled={isDeletingFree}
            >
              {isDeletingFree ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Deleting...
                </>
              ) : (
                'Delete Club'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Subscription Confirmation Dialog */}
        <Dialog
          open={showCancelDialog}
          onClose={() => setShowCancelDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Cancel Subscription
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to cancel your subscription?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your subscription will be canceled immediately, but your club will continue to be available until the end of the current subscription period.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelSubscription}
              variant="contained"
              color="error"
              disabled={isCanceling}
            >
              {isCanceling ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Canceling...
                </>
              ) : (
                'Cancel Subscription'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite Members CSV Dialog - edit mode only */}
        <Dialog
          open={showInviteDialog}
          onClose={() => { if (!isSendingInvites) { setShowInviteDialog(false); setCsvRows([]); setCsvParseError(''); setInviteResult(null); setManualName(''); setManualEmail(''); } }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Invite Members</DialogTitle>
          <DialogContent>
            {inviteResult ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Sent {inviteResult.sent} invitation{inviteResult.sent !== 1 ? 's' : ''}
                {inviteResult.skipped > 0 ? ` (${inviteResult.skipped} skipped — already members)` : ''}.
              </Alert>
            ) : (
              <>
                {clubType !== 'large' && (
                  <Alert severity={openSlots > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
                    {openSlots > 0
                      ? `${openSlots} open membership slot${openSlots !== 1 ? 's' : ''} available`
                      : 'Your club is at its member limit. Remove existing members before inviting new ones.'}
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a CSV with member names and email addresses. Each person will receive an invitation email
                  with a link to join your club. Existing members are automatically excluded.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Your CSV must have a header row with at least an <strong>email</strong> column.
                  Optional: <strong>name</strong> (or <strong>first name</strong> + <strong>last name</strong>) columns.
                </Typography>
                <Button variant="outlined" component="label" sx={{ mb: 2 }} disabled={isSendingInvites}>
                  Upload CSV
                  <input type="file" accept=".csv,text/csv" hidden onChange={handleCsvUpload} />
                </Button>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Or add people manually:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      label="Name"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      sx={{ flex: 1 }}
                      disabled={isSendingInvites}
                    />
                    <TextField
                      size="small"
                      label="Email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddManualRow(); } }}
                      sx={{ flex: 2 }}
                      disabled={isSendingInvites}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddManualRow}
                      disabled={!manualEmail.trim() || isSendingInvites}
                      sx={{ height: 40 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
                {csvParseError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{csvParseError}</Alert>
                )}
                {csvRows.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {csvRows.length} invitation{csvRows.length !== 1 ? 's' : ''} ready to send (existing members already excluded):
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell align="center" width={60}>Remove</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {csvRows.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell>{row.userFullName}</TableCell>
                              <TableCell>{row.userEmail}</TableCell>
                              <TableCell align="center">
                                <IconButton size="small" color="error" onClick={() => handleRemoveCsvRow(i)} disabled={isSendingInvites}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => { setShowInviteDialog(false); setCsvRows([]); setCsvParseError(''); setInviteResult(null); setManualName(''); setManualEmail(''); }}
              disabled={isSendingInvites}
            >
              {inviteResult ? 'Close' : 'Cancel'}
            </Button>
            {!inviteResult && (
              <Button
                variant="contained"
                onClick={handleSendInvites}
                disabled={csvRows.length === 0 || isSendingInvites || (clubType !== 'large' && openSlots <= 0)}
              >
                {isSendingInvites ? (
                  <><CircularProgress size={18} sx={{ mr: 1 }} />Sending…</>
                ) : (
                  `Send ${csvRows.length > 0 ? `${csvRows.length} ` : ''}Invitation${csvRows.length !== 1 ? 's' : ''}`
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Leave Club confirmation dialog */}
        <Dialog open={showLeaveDialog} onClose={() => !isLeaving && setShowLeaveDialog(false)}>
          <DialogTitle>Leave Club?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to leave <strong>{clubName}</strong>?
              You will lose access to all contests and will need to be re-invited to rejoin.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLeaveDialog(false)} disabled={isLeaving}>
              Cancel
            </Button>
            <Button onClick={handleLeaveClub} color="error" variant="contained" disabled={isLeaving}>
              {isLeaving ? 'Leaving...' : 'Leave Club'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

const ClubForm = () => {
  return (
    <Elements stripe={stripePromise}>
      <ClubFormContent />
    </Elements>
  );
};

export default ClubForm;